import { useEffect } from 'react';
import { useUIStore, VALID_SETTINGS_SECTION_IDS } from '@/core/stores/ui.store';
import { ProfileSection } from '../components/sections/ProfileSection';
import { IntegrationsSection } from '../components/sections/IntegrationsSection';
import { OrgInfoSection } from '../components/sections/OrgInfoSection';
import { ProjectInfoSection } from '../components/sections/ProjectInfoSection';
import { PlaceholderSection } from '../components/sections/PlaceholderSection';
import { TagsSection } from '@/features/tags/public';
import { StatusesSection } from '../components/sections/StatusesSection';
import { WorkScheduleSection } from '@/features/work-schedules/public';
import { FeatureErrorBoundary } from '@/core/error-boundaries/public';
import { sentryReporter } from '@/core/observability/public';

/**
 * Página principal de configuración — renderizada en `/:orgId/settings`.
 *
 * Muestra la sección correspondiente a `activeSettingsSectionId` del store.
 * La navegación entre secciones cambia únicamente el estado Zustand,
 * sin producir cambios de URL.
 *
 * @remarks
 * El `useEffect` de sincronización usa deps vacío intencionalmente.
 * Si dependiera de `activePanelId`, reactuaría a cada cambio de panel
 * y revertiría las acciones del `SidebarIconRail` mientras esta página
 * sigue montada. Solo se necesita sincronizar al montar (navegación directa por URL).
 *
 * La sanitización del `activeSettingsSectionId` cubre valores persistidos en
 * localStorage que pueden volverse inválidos entre versiones (ej: 'theme' o 'language'
 * que fueron eliminados como nav items independientes).
 */
export default function SettingsPage() {
  const setActivePanelId = useUIStore((s) => s.setActivePanelId);
  const activeSettingsSectionId = useUIStore((s) => s.activeSettingsSectionId);
  const setActiveSettingsSectionId = useUIStore((s) => s.setActiveSettingsSectionId);

  // Sincroniza el panel al montar — cubre navegación directa por URL.
  // Deps vacío intencional: solo se ejecuta al montar, no al cambiar activePanelId,
  // para no pelear con los botones del SidebarIconRail.

  useEffect(() => {
    setActivePanelId('settings');
  }, []);

  // Sanitiza valores de localStorage que ya no son secciones válidas

  useEffect(() => {
    if (!VALID_SETTINGS_SECTION_IDS.includes(activeSettingsSectionId)) {
      setActiveSettingsSectionId('profile');
    }
  }, []);

  const renderSection = () => {
    switch (activeSettingsSectionId) {
      case 'profile':
        return <ProfileSection />;
      case 'integrations':
        return <IntegrationsSection />;
      case 'org-info':
        return <OrgInfoSection />;
      case 'org-work-schedule':
        return <WorkScheduleSection />;
      case 'project-config':
        return <ProjectInfoSection />;
      case 'project-settings':
        return <TagsSection />;
      case 'project-statuses':
        return <StatusesSection />;
      default:
        return <PlaceholderSection />;
    }
  };

  return (
    <FeatureErrorBoundary reporter={sentryReporter}>
      <div className="mx-auto max-w-4xl" data-testid="settings-page">
        {renderSection()}
      </div>
    </FeatureErrorBoundary>
  );
}
